import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertTriangleIcon, CalendarClockIcon, FileCheck2Icon, ShieldCheckIcon } from 'lucide-react'

import { CORPORATE_KEY, corporateApi } from '@/api/corporate'
import type { Obligation } from '@/api/types'
import { RecordAnnualMeeting } from '@/components/corporate/record-annual-meeting'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { OBLIGATION_STATUS } from '@/lib/corporate'
import { formatDateOnly } from '@/lib/format'

export const Route = createFileRoute('/_app/corporate/')({
  component: CorporateDashboard,
})

/** Le dernier fait d'un genre : rapport annuel déposé, registre de l'art. 18.1 validé. */
function lastDone(obligations: Obligation[], kind: Obligation['kind']): Obligation | null {
  return (
    obligations
      .filter((o) => o.kind === kind && o.status === 'done' && o.done_on)
      .sort((a, b) => (b.done_on ?? '').localeCompare(a.done_on ?? ''))[0] ?? null
  )
}

/**
 * Le tableau de bord du livre (lot 45) : ce qui est en retard d'abord, les
 * prochaines obligations, le dernier rapport annuel et la dernière validation
 * du registre de l'art. 18.1.
 */
function CorporateDashboard() {
  const obligations = useQuery({
    queryKey: [...CORPORATE_KEY, 'obligations'],
    queryFn: () => corporateApi.obligations(),
  })
  // La dernière validation se lit à sa source : les validations du registre, la plus récente d'abord.
  const reviews = useQuery({
    queryKey: [...CORPORATE_KEY, 'control-reviews'],
    queryFn: corporateApi.controlReviews,
  })
  if (obligations.isPending) return <Skeleton className="h-48 w-full" />
  if (obligations.isError) {
    return <FormError>Les obligations de la société n’ont pas pu être lues.</FormError>
  }
  const items = obligations.data
  const overdue = items.filter((o) => o.status === 'overdue')
  const next = items
    .filter((o) => o.status === 'upcoming' || o.status === 'due_soon')
    .sort((a, b) => a.due_on.localeCompare(b.due_on))
    .slice(0, 5)
  const annualReturn = lastDone(items, 'annual_return')
  const lastReview = reviews.data?.[0] ?? null

  return (
    <div className="space-y-6" data-testid="tableau-livre">
      {overdue.length > 0 && (
        <Frame
          className="space-y-2 border-destructive/40 bg-destructive/5"
          data-testid="livre-en-retard"
        >
          <h2 className="flex items-center gap-2 font-semibold text-destructive-strong">
            <AlertTriangleIcon className="size-4" aria-hidden="true" />
            {overdue.length === 1
              ? 'Une obligation en retard'
              : `${String(overdue.length)} obligations en retard`}
          </h2>
          <ObligationList items={overdue} />
        </Frame>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <Fact
          icon={FileCheck2Icon}
          title="Dernier rapport annuel"
          testId="dernier-rapport-annuel"
          value={
            annualReturn?.done_on
              ? `Déposé le ${formatDateOnly(annualReturn.done_on)}`
              : 'Aucun inscrit au livre'
          }
        />
        <Fact
          icon={ShieldCheckIcon}
          title="Dernière validation du registre de l’art. 18.1"
          testId="derniere-validation-18-1"
          value={
            lastReview
              ? `Validé le ${formatDateOnly(lastReview.reviewed_on)}`
              : 'Aucune inscrite au livre'
          }
        />
      </div>
      <Frame className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 font-semibold">
            <CalendarClockIcon className="size-4 text-muted-foreground" aria-hidden="true" />
            Prochaines obligations
          </h2>
          <Link
            to="/corporate/calendrier"
            className="text-sm text-primary underline-offset-2 hover:underline"
          >
            Tout le calendrier
          </Link>
        </div>
        {next.length === 0 ? (
          <p className="text-sm text-muted-foreground">Rien à l’horizon.</p>
        ) : (
          <ObligationList items={next} />
        )}
      </Frame>
    </div>
  )
}

function Fact({
  icon: Icon,
  title,
  value,
  testId,
}: {
  icon: typeof FileCheck2Icon
  title: string
  value: string
  testId: string
}) {
  return (
    <Frame className="flex items-start gap-3" data-testid={testId}>
      <Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="font-medium">{value}</p>
      </div>
    </Frame>
  )
}

function ObligationList({ items }: { items: Obligation[] }) {
  return (
    <ul className="divide-y text-sm">
      {items.map((obligation) => {
        const status = OBLIGATION_STATUS[obligation.status]
        return (
          <li
            key={obligation.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
            data-testid="obligation"
          >
            <span className="w-28 shrink-0 tabular-nums">{formatDateOnly(obligation.due_on)}</span>
            <span className="min-w-0 flex-1 font-medium">{obligation.title}</span>
            <Badge variant={status.tone}>{status.label}</Badge>
            {obligation.legal_reference && (
              <span className="w-full pl-31 text-xs text-muted-foreground">
                {obligation.legal_reference}
              </span>
            )}
            <RecordAnnualMeeting obligation={obligation} />
          </li>
        )
      })}
    </ul>
  )
}
